import type { FilterDefinitionValue } from "@/pages/(app)/returns/hooks/use-filter-definitions";
import type {
  DateFrontFilterValue,
  DateFrontFilterValueType,
} from "@/pages/(app)/returns/providers/return-requests-filters.provider";
import { useReturnRequestsFilters } from "@/pages/(app)/returns/providers/use-return-requests-filters";
import { DateFilterCondition, DateFilterName } from "../../utils/types";
import { endOfDay, startOfDay, subDays, subMonths } from "date-fns";
import { DateRangePicker } from "@babackapp/ui/components/date-range-picker";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@babackapp/ui/components/command";
import { Check } from "lucide-react";

interface DateFilterCommandProps {
  filterDefinition: FilterDefinitionValue;
  filter?: DateFrontFilterValue;
  filterKey: DateFilterName;
}

function getDateRangeFromPreset(
  preset: DateFrontFilterValueType,
): [string, string] {
  const now = new Date();
  let from: Date;
  switch (preset) {
    case "1d":
      from = subDays(Date.now(), 1);
      break;
    case "3d":
      from = subDays(Date.now(), 3);
      break;
    case "1w":
      from = subDays(Date.now(), 7);
      break;
    case "2w":
      from = subDays(Date.now(), 14);
      break;
    case "3w":
      from = subDays(Date.now(), 21);
      break;
    case "1m":
      from = subMonths(Date.now(), 1);
      break;
    case "2m":
      from = subMonths(Date.now(), 2);
      break;
    case "3m":
      from = subMonths(Date.now(), 3);
      break;
    case "6m":
      from = subMonths(Date.now(), 6);
      break;
    default:
      from = now;
  }
  return [
    startOfDay(from).getTime().toString(),
    endOfDay(now).getTime().toString(),
  ];
}

export default function DateFilterCommand({
  filterDefinition,
  filter,
  filterKey,
}: DateFilterCommandProps) {
  const t = useTranslation();
  const { addFilter, clearFilter } = useReturnRequestsFilters();
  const condition: DateFilterCondition = "between";
  const options = Array.from(filterDefinition.options.values());

  // Parse existing filter values if available
  const fromTimestamp = filter?.value?.[0]
    ? parseInt(filter.value[0])
    : undefined;
  const toTimestamp = filter?.value?.[1]
    ? parseInt(filter.value[1])
    : undefined;

  // Function to add date range filter
  const addDateRangeFilter = (from: number, to: number) => {
    clearFilter({
      name: filterKey,
    });
    addFilter({
      name: filterKey,
      condition: condition,
      value: [from.toString(), to.toString()],
      isMulti: true,
    });
  };

  return (
    <Command className="w-full">
      <CommandInput placeholder={t("misc.filters.title")} />
      <CommandList>
        <CommandEmpty>{t("misc.noResultFound")}</CommandEmpty>
        <CommandGroup>
          {/*<div className="px-2 py-1 text-xs font-medium text-muted-foreground">*/}
          {/*  {t("misc.filters.presets")}*/}
          {/*</div>*/}

          {options.map((option) => (
            <CommandItem
              key={option.value}
              onSelect={() => {
                // Si value est un preset, on calcule from/to
                const [from, to] = getDateRangeFromPreset(
                  option.value as DateFrontFilterValueType,
                );
                clearFilter({
                  name: filterKey,
                });
                addFilter({
                  name: filterKey,
                  condition: condition,
                  value: [from, to],
                  isMulti: true,
                });
              }}
              className="flex-row items-center justify-between"
            >
              <div className="flex flex-row flex-1 items-center">
                {option.Icon && (
                  <div className="mr-1">
                    <option.Icon className="h-4 w-4" />
                  </div>
                )}
                <span>{option.label}</span>
              </div>
              <div
                className={
                  filter?.value?.[0] ===
                  getDateRangeFromPreset(
                    option.value as DateFrontFilterValueType,
                  )[0]
                    ? ""
                    : "hidden"
                }
              >
                <Check className="h-4 w-4" />
              </div>
            </CommandItem>
          ))}
          <CommandItem
            onSelect={() => {}}
            className="flex-row items-center justify-between"
          >
            <DateRangePicker
              range={
                fromTimestamp && toTimestamp
                  ? { from: fromTimestamp, to: toTimestamp }
                  : undefined
              }
              onSelect={(dateRange) => {
                if (dateRange.from && dateRange.to) {
                  addDateRangeFilter(dateRange.from, dateRange.to);
                }
              }}
              onRemove={() => {
                clearFilter({
                  name: filterKey,
                });
              }}
              customButton={
                <div className="flex flex-row flex-1 items-center">
                  {/*<div className="mr-1">*/}
                  {/*  <Check className="h-4 w-4" />*/}
                  {/*</div>*/}
                  <span>{t("misc.filters.custom")}</span>
                </div>
              }
            />
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup>
          <CommandItem
            onSelect={() => {
              clearFilter({
                name: filterKey,
              });
            }}
            className="justify-center text-center"
          >
            {t("misc.filters.clear")}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
